import { Models } from "api/types";

/**
 * A requirement row always carries a minimum quantity, and one unit is the value that states nothing — it is
 * what an ordinary "$10 off Botox" writes. Only a threshold above a single unit changes what a shopper sees:
 * it suppresses the promo price here, and makes the badge and the PDP banner state the threshold instead.
 */
export const SINGLE_UNIT_THRESHOLD = 1;

/**
 * Calculates the discounted price for deterministic discounts (percentage or amount off).
 * Returns null for variable discounts, BXGY discounts, quantity-threshold discounts, or when no
 * discount applies.
 *
 * @param basePriceInCents - The original price in cents
 * @param discount - The discount data from the shop
 * @returns The discounted price in cents, or null if no deterministic discount applies
 */
export const getDeterministicDiscountedPrice = (
  basePriceInCents: number,
  discount?: Models<"ShopProductDiscountModel"> | null,
): number | null => {
  if (!discount || discount.isVariable || discount.isBuyXGetY === true) {
    return null;
  }

  // "$10 off Botox when you buy 20" only reaches this price at twenty units, so a single-unit card has no
  // deterministic price to strike through — the badge states the threshold instead.
  const requiresMultipleUnits =
    typeof discount.minimumQuantity === "number" &&
    discount.minimumQuantity > SINGLE_UNIT_THRESHOLD;

  if (requiresMultipleUnits) {
    return null;
  }

  const percentageOff = discount.maxPercentageOff ?? 0;
  const amountOff = discount.maxAmountOff ?? 0;

  if (percentageOff <= 0 && amountOff <= 0) {
    return null;
  }

  const priceAfterPercentage =
    percentageOff > 0 ? Math.round(basePriceInCents * (1 - percentageOff)) : basePriceInCents;
  const priceAfterAmount =
    amountOff > 0 ? Math.max(0, basePriceInCents - amountOff) : basePriceInCents;

  return Math.min(priceAfterPercentage, priceAfterAmount);
};
